import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import _noop from 'lodash-es/noop';
import { orderBy } from 'lodash-es';
import Search from 'sushiweb/Icons/all/Search';
import {
  includesOption,
  indexOfOption,
  searchWithName,
} from '../../../helpers';
import {
  SearchContainerV2,
  SearchInput,
  TableContainer,
} from '../../common/styles';
import DataTables from '../../common/components/DataTables/DataTables';
import { previousTopupsHeadColumns } from '../../common/components/DataTables/components/headColumns';
import ErrorPage from '../../common/components/ErrorPage';
import {
  REFRESH_LABEL,
  applicableFlowTypes,
  currentFlowTypeEnum,
} from '../../common/components/constants';
import StatusFilter from '../../common/components/StatusFilter';

const PreviousTopups = ({
  currentFlow,
  selectedWorkspace,
  previousTopupsData,
  fetchingPreviousTopups,
  fetchedPreviousTopups,
  getPreviousTopups,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [tableData, setTableData] = useState([]);
  const [currentData, setCurrentData] = useState([]);
  const [loading, setLoading] = useState(true);
  const keyFields = ['program_name'];
  const [search, onInputChange] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [typingTimeout, setTypingTimeout] = useState(0);
  const previousTopups = previousTopupsData;
  const [currentFilters, setCurrentFilters] = useState([]);
  const [isSearchClicked, setIsSearchClicked] = useState(false);
  const [disabledSearch, setDisabledSearch] = useState(false);
  const [filterStatusOptions, setFilterStatusOptions] = useState([
    {
      value: [5],
      label: 'PROCESSING_IN_PROGRESS',
      disabled: true,
    },
    {
      value: [6],
      label: 'PARTIALLY_DONE',
      disabled: true,
    },
    {
      value: [7],
      label: 'FAILED',
      disabled: true,
    },
    {
      value: [8, 9, 10, 11, 12],
      label: 'SUCCESS',
      disabled: true,
    },
  ]);
  const filterFunc = val => {
    let flag = false;
    currentFilters.forEach(filter => {
      for (let i = 0; i < filter.value.length; i += 1)
        if (val.status === filter.value[i]) flag = true;
    });
    return flag;
  };

  const statusInitiatedFilter = val => {
    if (val.total_amount.value !== 0) {
      return true;
    }
    return false;
  };

  const enableCurrentFilters = availableStatuses => {
    for (let i = 0; i < filterStatusOptions.length; i += 1) {
      for (let j = 0; j < filterStatusOptions[i].value.length; j += 1) {
        if (availableStatuses.includes(filterStatusOptions[i].value[j])) {
          filterStatusOptions[i].disabled = false;
          break;
        } else filterStatusOptions[i].disabled = true;
      }
    }
    setFilterStatusOptions(filterStatusOptions);
  };

  useEffect(() => {
    if (selectedWorkspace && selectedWorkspace.workspace_id)
      getPreviousTopups({
        workspaceId: selectedWorkspace.workspace_id,
        flowType: currentFlow,
      });
  }, [selectedWorkspace, currentFlow]);

  useEffect(() => {
    if (tableData.length > 0) {
      const start = currentPage * 10 - 10;
      const end = currentPage * 10;
      const rangeData = tableData.slice(start, end);
      setCurrentData(rangeData);
    }
  }, [currentPage]);

  useEffect(() => {
    if (!fetchingPreviousTopups && fetchedPreviousTopups) {
      if (previousTopups) {
        previousTopups.sort(
          (a, b) => new Date(b.scheduled_date) - new Date(a.scheduled_date),
        );
        const filtererdPreviousTopus = previousTopups.filter(
          statusInitiatedFilter,
        );
        if (filtererdPreviousTopus.length === 0) {
          setDisabledSearch(true);
        }
        setTableData(filtererdPreviousTopus);
        setSearchResults(filtererdPreviousTopus);
        setCurrentData(filtererdPreviousTopus.slice(0, 10));
      }
      setLoading(false);
    }
  }, [fetchingPreviousTopups]);

  useEffect(() => {
    if (typingTimeout) {
      clearTimeout(typingTimeout);
    }
    const timeout = setTimeout(() => {
      if (!fetchingPreviousTopups) {
        const arr = searchWithName(previousTopups, search, keyFields);
        const initiatedFilteredArr = arr.filter(statusInitiatedFilter);
        const availableStatuses = [];
        initiatedFilteredArr.forEach(data => {
          if (!availableStatuses.includes(data.status)) {
            availableStatuses.push(data.status);
          }
        });
        enableCurrentFilters(availableStatuses);
        if (currentFilters.length !== 0) {
          const filteredSearchArr = initiatedFilteredArr.filter(filterFunc);
          setSearchResults(filteredSearchArr);
        } else {
          setSearchResults(initiatedFilteredArr);
        }
        handlePageChange(1);
      }
    }, 500);

    return () => setTypingTimeout(timeout);
  }, [search, currentFilters, fetchingPreviousTopups]);

  useEffect(() => {
    if (searchResults) {
      const start = currentPage * 10 - 10;
      const end = currentPage * 10;
      const rangeData = searchResults.slice(start, end);
      setCurrentData(rangeData);
    }
  }, [currentPage, searchResults]);

  const handlePageChange = page => {
    setCurrentPage(page);
  };

  const handleSort = (column, sortDirection) => {
    setSearchResults(orderBy(searchResults, column.selector, sortDirection));
  };

  const refreshPage = () => {
    getPreviousTopups({
      workspaceId: selectedWorkspace.workspace_id,
      flowType: currentFlow,
    });
  };
  const handleSelect = val => {
    const newCurrentFilters = [...currentFilters];
    if (includesOption(newCurrentFilters, val)) {
      const index = indexOfOption(newCurrentFilters, val);
      newCurrentFilters.splice(index, 1);
    } else {
      newCurrentFilters.push(val);
    }
    setCurrentFilters(newCurrentFilters);
  };
  const clearFilters = () => {
    setCurrentFilters([]);
  };

  return (
    <>
      <SearchContainerV2>
        <SearchInput
          leftIcon={<Search />}
          innerWidth='100%'
          value={search}
          onChange={onInputChange}
          onFocus={() => setIsSearchClicked(true)}
          onBlur={() => setIsSearchClicked(false)}
          label={
            isSearchClicked || disabledSearch || search?.length ? (
              'Search by meal plan name'
            ) : (
              <span style={{ fontSize: '16px', fontWeight: 300 }}>
                {' '}
                Search by meal plan name
                {' '}
              </span>
            )
          }
          disabled={disabledSearch}
        />
        <StatusFilter
          isDisabled={disabledSearch}
          clearFilters={clearFilters}
          currentFilters={currentFilters}
          options={filterStatusOptions}
          handleSelect={handleSelect}
        />
      </SearchContainerV2>
      <TableContainer>
        {!fetchedPreviousTopups ? (
          <ErrorPage
            showAction
            actionLabel={REFRESH_LABEL}
            clickAction={refreshPage}
          />
        ) : (
          <DataTables
            data={currentData}
            columns={previousTopupsHeadColumns}
            loading={fetchingPreviousTopups || loading}
            pagination
            handlePageChange={handlePageChange}
            totalRows={searchResults ? searchResults.length : 0}
            onSort={handleSort}
            emptyStateTitle='No previous-topups'
          />
        )}
      </TableContainer>
    </>
  );
};

PreviousTopups.propTypes = {
  currentFlow: PropTypes.oneOf(applicableFlowTypes),
  selectedWorkspace: PropTypes.objectOf(PropTypes.any),
  previousTopupsData: PropTypes.objectOf(PropTypes.any),
  getPreviousTopups: PropTypes.func,
  fetchingPreviousTopups: PropTypes.bool,
  fetchedPreviousTopups: PropTypes.bool,
};

PreviousTopups.defaultProps = {
  currentFlow: currentFlowTypeEnum.FLOW_TYPE_PPI,
  selectedWorkspace: {},
  previousTopupsData: {},
  getPreviousTopups: _noop,
  fetchingPreviousTopups: true,
  fetchedPreviousTopups: true,
};

export default PreviousTopups;
